import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { ReadableNavButton } from "../Sticker";
import LinkifiedText from "../LinkifiedText";

const MAX_LENGTH = 500;

export default function BroadcastPanel({
  eventId,
  occurrenceDate,
  tokenParam,
  rsvpCount = 0,
  demoMode = false,
  demoBroadcasts = null,
  onDemoBroadcast,
  onSent,
}) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [recalling, setRecalling] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const hasRsvps = rsvpCount > 0;
  const activeCount = history.filter((b) => !b.recalled_at).length;
  const trimmed = message.trim();
  const canSend = hasRsvps && trimmed.length > 0 && !sending;

  const loadHistory = useCallback(async () => {
    if (!eventId) return;
    if (demoMode) {
      setHistory(demoBroadcasts || []);
      setLoadingHistory(false);
      return;
    }
    setLoadingHistory(true);
    try {
      const r = await api.get(`/events/${eventId}/broadcasts`, {
        params: {
          token: tokenParam || undefined,
          occurrence_date: occurrenceDate || undefined,
        },
      });
      setHistory(r.data.broadcasts || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoadingHistory(false);
    }
  }, [eventId, occurrenceDate, tokenParam, demoMode, demoBroadcasts]);

  useEffect(() => {
    setMessage("");
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!demoMode) return;
    setHistory(demoBroadcasts || []);
  }, [demoMode, demoBroadcasts]);

  async function send() {
    if (!canSend) return;
    setSending(true);
    try {
      if (demoMode) {
        onDemoBroadcast?.({ type: "send", message: trimmed });
        toast.success("Update sent to attendees.");
        setMessage("");
        setHistory((prev) => [
          {
            id: `demo-broadcast-${Date.now()}`,
            message: trimmed,
            created_at: new Date().toISOString(),
            recalled_at: null,
          },
          ...prev,
        ]);
        onSent?.();
        return;
      }
      await api.post(
        `/events/${eventId}/broadcasts`,
        { occurrence_date: occurrenceDate, message: trimmed },
        { params: { token: tokenParam || undefined } },
      );
      toast.success("Update sent to attendees.");
      setMessage("");
      await loadHistory();
      onSent?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  }

  async function recall(mode) {
    setRecalling(mode);
    try {
      if (demoMode) {
        onDemoBroadcast?.({ type: "recall", mode });
        const count =
          mode === "all"
            ? history.filter((b) => !b.recalled_at).length
            : history.some((b) => !b.recalled_at)
              ? 1
              : 0;
        setHistory((prev) => {
          if (mode === "all") {
            const now = new Date().toISOString();
            return prev.map((b) => (b.recalled_at ? b : { ...b, recalled_at: now }));
          }
          const idx = prev.findIndex((b) => !b.recalled_at);
          if (idx < 0) return prev;
          const updated = [...prev];
          updated[idx] = { ...updated[idx], recalled_at: new Date().toISOString() };
          return updated;
        });
        toast.success(count ? `Recalled ${count} update${count === 1 ? "" : "s"}.` : "Nothing to recall.");
        onSent?.();
        return;
      }
      const path =
        mode === "all"
          ? `/events/${eventId}/broadcasts/recall-all`
          : `/events/${eventId}/broadcasts/recall-last`;
      const r = await api.post(
        path,
        { occurrence_date: occurrenceDate },
        { params: { token: tokenParam || undefined } },
      );
      const count = r.data.recalled_count || 0;
      toast.success(count ? `Recalled ${count} update${count === 1 ? "" : "s"}.` : "Nothing to recall.");
      await loadHistory();
      onSent?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRecalling(null);
    }
  }

  return (
    <section className="space-y-4" aria-labelledby="broadcast-panel-title" data-testid="broadcast-panel">
      <header className="space-y-1">
        <h2 id="broadcast-panel-title" className="font-ui font-semibold text-[18px] sm:text-[20px] leading-tight">
          Broadcast App Notification
        </h2>
        <p className="font-ui font-medium text-[14px] text-ink/70 leading-snug">
          Sends a push / in-app notification to all RSVPs for this date (e.g., venue changes, schedule updates, entry
          instructions).
        </p>
      </header>

      {!hasRsvps && (
        <p
          className="font-ui font-medium text-[14px] text-ink/65"
          data-testid="broadcast-no-rsvps-hint"
          role="status"
        >
          No RSVPs for this date yet. Share your event link to get started.
        </p>
      )}

      <label className="block">
        <span className="font-ui font-medium text-[14px] text-ink/80">Message</span>
        <textarea
          className="field mt-2 min-h-[120px] disabled:opacity-60 disabled:cursor-not-allowed"
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MAX_LENGTH))}
          placeholder="e.g. Doors open 30 minutes early tonight!"
          data-testid="broadcast-message-input"
          maxLength={MAX_LENGTH}
          disabled={!hasRsvps}
        />
        <p className="font-ui text-[12px] font-medium text-ink/55 mt-1 tabular-nums">
          {message.length} / {MAX_LENGTH}
        </p>
      </label>

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <ReadableNavButton
          type="button"
          variant="primary"
          onClick={send}
          disabled={!canSend}
          className="w-full sm:w-auto"
          data-testid="broadcast-send-btn"
        >
          {sending ? "Sending…" : "Send update"}
        </ReadableNavButton>
        <ReadableNavButton
          type="button"
          onClick={() => recall("last")}
          disabled={recalling || activeCount === 0}
          className="w-full sm:w-auto"
          data-testid="broadcast-recall-last-btn"
        >
          {recalling === "last" ? "Recalling…" : "Recall last"}
        </ReadableNavButton>
        <ReadableNavButton
          type="button"
          onClick={() => recall("all")}
          disabled={recalling || activeCount === 0}
          className="w-full sm:w-auto"
          data-testid="broadcast-recall-all-btn"
        >
          {recalling === "all" ? "Recalling…" : "Recall all"}
        </ReadableNavButton>
      </div>

      <div className="space-y-2 pt-1">
        <p className="font-ui font-semibold text-[15px]">Recent broadcasts</p>
        {loadingHistory ? (
          <p className="font-ui font-medium text-[14px] text-ink/60">Loading…</p>
        ) : history.length === 0 ? (
          <p className="font-ui font-medium text-[14px] text-ink/60">No broadcasts yet for this date.</p>
        ) : (
          <ul className="space-y-2" data-testid="broadcast-history-list">
            {history.map((item) => (
              <li
                key={item.id}
                className={`rounded-md2 border border-black/[0.08] p-3 ${
                  item.recalled_at ? "bg-cream/60 opacity-70" : "bg-white"
                }`}
                data-testid={`broadcast-history-${item.id}`}
              >
                <p className="font-ui text-[14px] font-medium text-ink/80 break-words [overflow-wrap:anywhere] whitespace-pre-line">
                  <LinkifiedText>{item.message}</LinkifiedText>
                </p>
                <p className="font-ui text-[12px] text-ink/55 mt-1">
                  {item.recalled_at ? "Recalled · " : ""}
                  {item.created_at
                    ? new Date(item.created_at).toLocaleString("en-GB", {
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : ""}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
